import logging from '@sstdev/lib_logging';

/**
 * The disputed ids travel to the server in the query string, so one request has to stay inside a
 * sane URL length. More than this many are split across several requests rather than abandoned.
 */
export const MAX_DISPUTED_IDS_PER_REQUEST = 100;

/**
 * A ceiling on how much disagreement is worth settling in one pass. Reaching it means the two
 * sides differ so widely that the local view is unlikely to be merely slightly stale, and firing
 * an unbounded number of requests to find out would be worse than saying so.
 */
export const MAX_DISPUTED_IDS_TO_RESOLVE = 1000;

/**
 * Settle a disagreement between a stage-2 server page and the rows the view already matched
 * locally (#1978).
 *
 * The gate (#332) constrains the view to the _ids a server page returned, which silently deletes
 * any correct row that page omitted. It cannot simply keep the omitted rows either: a local row
 * can match on values that are stale, deleted server-side, or optimistically written and never
 * committed, so the client is sometimes the broken side.
 *
 * Neither side's matcher is trusted. The server is authoritative over the field VALUES, the
 * user's filter is the loki view's own predicate, so this refreshes the disputed rows BY _id
 * with the find criteria deliberately left off and lets the view re-evaluate the user's filter
 * against what came back. A row is confirmed only when the refresh returned it (so a row deleted
 * or out of the caller's scope is dropped however fresh the local copy looks), the refreshed
 * values actually reached the local collection, AND the view still matches it afterwards (so a
 * stale or uncommitted local value is dropped once the real value lands).
 *
 * When the disagreement cannot be settled — too many rows, or a refresh that failed — this
 * reports `incomplete` rather than an empty confirmation. The two are not the same thing:
 * "nothing survived the check" means gate on the server page, while "the check could not be run"
 * means we have no basis for deleting the rows, and the caller keeps the view ungated until the
 * next refresh. Returning `[]` for both is what made the original defect reappear above the
 * request-size limit.
 *
 * @param {object} args
 * @param {object} args.dbView the view whose stage-2 page is about to be applied as a gate.
 * @param {string} args.queryId the queryId this stage-2 fetch belongs to.
 * @param {string[]} args.pageIds the _ids the server page returned.
 * @param {Function} args.fetchAndUpsert fetchAndUpsertTemporaryQueryResult, passed in so each
 * dbViews variant keeps its own spy point.
 * @returns {Promise<{ confirmedIds: string[], incomplete: boolean }>} the disputed _ids that
 * survived the check, and whether the disagreement was left unsettled.
 */
export default async function resolveGateDisagreement({ dbView, queryId, pageIds, fetchAndUpsert }) {
    const { lokiView, viewName } = dbView;
    // Set membership, not Array.includes: a Find page can hold thousands of local rows and a
    // server page up to `pageSizeForLimitSyncSizeRelations` (1000).
    const returnedIds = new Set(pageIds);
    const disputedIds = getViewIds(lokiView).filter(_id => !returnedIds.has(_id));
    if (disputedIds.length === 0) return settled([]);

    if (disputedIds.length > MAX_DISPUTED_IDS_TO_RESOLVE) {
        logging.warn(
            `[DBVIEW] ${viewName} holds ${disputedIds.length} matching rows the server page omitted — beyond ` +
                `${MAX_DISPUTED_IDS_TO_RESOLVE}, too wide a disagreement to settle in one pass; leaving the view ungated.`
        );
        return unsettled();
    }

    logging.info(
        `[DBVIEW] ${viewName}: the server's complete answer omits ${disputedIds.length} row(s) the view matched ` +
            'locally; refreshing them to settle it.'
    );

    // One request per batch, because the ids travel in the query string. Sequential rather than
    // parallel: this runs while the user is looking at a grid, and a burst of requests to settle a
    // display question is not worth the latency it would cost everything else on the connection.
    const refreshedIds = new Set();
    for (const batch of chunk(disputedIds, MAX_DISPUTED_IDS_PER_REQUEST)) {
        let batchResult;
        try {
            batchResult = await fetchAndUpsert(dbView, queryId, batch);
        } catch (err) {
            // Without an answer there is no way to tell a correct row from a stale one, and
            // deleting the rows on the strength of a request that failed is the defect this
            // function exists to fix.
            logging.warn(
                `[DBVIEW] Could not resolve the stage-2 disagreement for ${viewName}; leaving the view ungated.`
            );
            logging.error(err);
            return unsettled();
        }
        // A record the server returned but that did not reach the local collection still holds its
        // old values, so confirming it would keep exactly the stale row this check is meant to
        // catch. bulkUpsert reports those per-record failures through a count rather than by
        // rejecting, which is why fetchAndUpsertTemporaryQueryResult reports the ids.
        const failedIds = new Set(batchResult?.failedIds ?? []);
        for (const record of batchResult?.result ?? []) {
            if (record?._id && !failedIds.has(record._id)) refreshedIds.add(record._id);
        }
    }

    // The user can change filters while the refresh is in flight. Never keep rows on behalf of a
    // query that has already been replaced.
    if (dbView.queryId !== queryId) {
        logging.debug(`[DBVIEW] Discarding a stale stage-2 disagreement resolution for ${viewName}.`);
        return unsettled();
    }

    // The refresh upserted the server's current values, so the view has re-evaluated the user's
    // own filter against them.
    const stillMatchingIds = new Set(getViewIds(lokiView));
    return settled(disputedIds.filter(_id => refreshedIds.has(_id) && stillMatchingIds.has(_id)));
}

function settled(confirmedIds) {
    return { confirmedIds, incomplete: false };
}

function unsettled() {
    return { confirmedIds: [], incomplete: true };
}

function chunk(items, size) {
    const batches = [];
    for (let i = 0; i < items.length; i += size) {
        batches.push(items.slice(i, i + size));
    }
    return batches;
}

function getViewIds(lokiView) {
    // `data()` resolves any pending loki filtering before returning, so this is the view's
    // current match set rather than a snapshot taken before the last upsert.
    return (lokiView?.data?.() ?? []).map(r => r?._id).filter(Boolean);
}
